import { Bell, BellOff, Settings2, type LucideIcon } from "lucide-react";
import type { QueueSummary } from "../../../experience-shell/queues";

/**
 * Row-scoped actions of the queue rail.
 *
 * The descriptor list is the single source of truth for menu order, labels,
 * and icons. A new action is one appended entry.
 */

export interface QueueRowActionContext {
  readonly onManage?: (queue: QueueSummary) => void;
  readonly mutedQueueIds?: ReadonlySet<string>;
  readonly onToggleMute?: (queue: QueueSummary) => void;
}

export interface QueueRowAction {
  readonly id: "manage" | "toggle-mute";
  /** Label and icon resolve per render so Mute/Unmute presents the right verb. */
  readonly label: (context: QueueRowActionContext, queue: QueueSummary) => string;
  readonly icon: (context: QueueRowActionContext, queue: QueueSummary) => LucideIcon;
  readonly isAvailable: (context: QueueRowActionContext) => boolean;
  readonly invoke: (context: QueueRowActionContext, queue: QueueSummary) => void;
}

export const QUEUE_ROW_ACTIONS: readonly QueueRowAction[] = [
  {
    id: "manage",
    label: () => "Queue maintenance",
    icon: () => Settings2,
    isAvailable: (context) => context.onManage !== undefined,
    invoke: (context, queue) => context.onManage?.(queue),
  },
  {
    id: "toggle-mute",
    label: (context, queue) =>
      context.mutedQueueIds?.has(queue.id) === true
        ? "Unmute notifications"
        : "Mute notifications",
    icon: (context, queue) => (context.mutedQueueIds?.has(queue.id) === true ? Bell : BellOff),
    isAvailable: (context) => context.onToggleMute !== undefined,
    invoke: (context, queue) => context.onToggleMute?.(queue),
  },
];

export function availableQueueRowActions(context: QueueRowActionContext): QueueRowAction[] {
  return QUEUE_ROW_ACTIONS.filter((action) => action.isAvailable(context));
}
